import { computed, signal } from '@angular/core';
import {
  IQBoostConversationStore,
  IQChat,
  IQChatStep,
  LangGraphConfirmation,
  LangGraphHandoff,
  LangGraphStreamUpdate,
  LangGraphToolCallRef,
} from '@maximizer/ai/data-access';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { IQBoostChatOverride, IQBoostClaimedRun } from '../overrides/iqboost-chat-override';
import { presentAnswer } from './iqboost-chat-history';

export interface IQBoostRunningStep extends IQChatStep {
  id: string | null;
}

export interface IQBoostChatRunOwner {
  readonly conversation: IQBoostConversationStore;
  readonly overrides: readonly IQBoostChatOverride[];
}

export interface IQBoostChatRunOptions {
  readonly claimed?: IQBoostClaimedRun | null;
  // The question it answers, as the conversation shows it.
  readonly question?: IQChat | null;
  // Joined after the page that started it was gone: the thread, not the stream, has its answer.
  readonly joined?: boolean;
  // Answers the confirmation an earlier run stopped on, under that run's question.
  readonly resumed?: boolean;
}

export class IQBoostChatRun {
  // Null until the first question of a new chat has created its thread.
  readonly threadId = signal<string | null>(null);
  // The AI message streaming now: the answer, unless a tool call follows it.
  readonly answer = signal('');
  readonly reasoning = signal<string | null>(null);
  readonly steps = signal<IQChatStep[]>([]);
  readonly running = signal<IQBoostRunningStep[]>([]);
  readonly handoffs = signal<LangGraphHandoff[]>([]);
  readonly confirmations = signal<LangGraphConfirmation[]>([]);
  readonly waiting = computed(() =>
    this.confirmations().some(({ data }) => data.status === 'pending'),
  );
  readonly shownAnswer = computed(() => {
    const answer = this.answer();
    return answer ? this.present(answer) : null;
  });
  readonly claimed: IQBoostClaimedRun | null;
  readonly question: IQChat | null;
  readonly joined: boolean;
  readonly resumed: boolean;
  // Set by the `end` event only: a stream can close before the run is over.
  ended = false;
  failed = false;
  cancelled = false;
  // The chat above the run as last shown, so its conversation shows again as it was left.
  history: IQChat[] | null = null;

  private messageId = '';
  // The restored copy of a model turn reports its tool calls again, with the same ids.
  private readonly toolCallIds = new Set<string>();
  private readonly resultIds = new Set<string>();

  constructor(
    readonly owner: IQBoostChatRunOwner,
    { claimed = null, question = null, joined = false, resumed = false }: IQBoostChatRunOptions = {},
  ) {
    this.claimed = claimed;
    this.question = question;
    this.joined = joined;
    this.resumed = resumed;
  }

  apply(update: LangGraphStreamUpdate): void {
    switch (update.kind) {
      case 'text':
        if (update.messageId !== this.messageId) {
          // A new AI message means the previous one was not the final answer.
          this.demoteAnswerToReasoning();
          this.messageId = update.messageId;
        }
        this.answer.update((answer) => answer + update.text);
        break;
      case 'toolCall':
        this.demoteAnswerToReasoning();
        this.startSteps(update.tools);
        break;
      case 'toolResult':
        this.completeStep(update.toolName, update.toolCallId);
        break;
      case 'handoff':
        if (!this.handoffs().some(({ id }) => id === update.handoff.id)) {
          this.handoffs.update((handoffs) => [...handoffs, update.handoff]);
        }
        break;
      case 'confirmation':
        // A resumed run sends the confirmation again before its decision: the latest one stands.
        this.confirmations.update((confirmations) => [
          ...confirmations.filter(({ id }) => id !== update.confirmation.id),
          update.confirmation,
        ]);
        break;
      case 'end':
        this.ended = true;
        break;
    }
  }

  fail(): void {
    this.failed = true;
    this.answer.set('');
  }

  // A run joined again is replayed from its first event.
  restart(): void {
    this.answer.set('');
    this.reasoning.set(null);
    this.steps.set([]);
    this.running.set([]);
    this.handoffs.set([]);
    this.confirmations.set([]);
    this.messageId = '';
    this.toolCallIds.clear();
    this.resultIds.clear();
  }

  private present(answer: string): string {
    return presentAnswer(this.owner.overrides, answer);
  }

  private demoteAnswerToReasoning(): void {
    const answer = this.answer();
    if (isNullOrEmpty(answer)) {
      return;
    }
    const shown = this.present(answer);
    this.reasoning.update((previous) => (previous ? `${previous}\n\n${shown}` : shown));
    this.answer.set('');
    this.messageId = '';
  }

  private startSteps(tools: LangGraphToolCallRef[]): void {
    const running = this.running();
    const started = tools.filter((call) =>
      call.id === null
        ? !running.some((step) => step.id === null && step.tool === call.name)
        : !this.toolCallIds.has(call.id),
    );
    if (started.length === 0) {
      return;
    }
    started.forEach((call) => call.id && this.toolCallIds.add(call.id));
    this.running.set([...running, ...started.map((call) => ({ id: call.id, tool: call.name }))]);
  }

  private completeStep(toolName: string, toolCallId: string | null): void {
    if (toolCallId) {
      if (this.resultIds.has(toolCallId)) {
        return;
      }
      this.resultIds.add(toolCallId);
    }

    const running = this.running();
    const byId = toolCallId ? running.findIndex((step) => step.id === toolCallId) : -1;
    const index = byId >= 0 ? byId : running.findIndex((step) => step.tool === toolName);
    const tool = toolName || running[index]?.tool;
    if (index >= 0) {
      this.running.set(running.filter((_, position) => position !== index));
    }
    if (tool) {
      this.steps.update((steps) => [...steps, { tool }]);
    }
  }
}
